
import React, { useEffect, useRef } from 'react';
import { ArrowRight, BrainCircuit, Zap, Users } from 'lucide-react';
import { Button } from "@/components/ui/button";

const HeroSection = () => {
  const featureCardsRef = useRef<HTMLDivElement>(null);

  // Animation effect for staggered entrance of feature cards
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const cards = featureCardsRef.current?.querySelectorAll('.feature-card');
          cards?.forEach((card, index) => {
            setTimeout(() => {
              card.classList.add('animate-fade-in');
            }, index * 200);
          });
          observer.disconnect();
        }
      });
    }, { threshold: 0.3 });

    if (featureCardsRef.current) {
      observer.observe(featureCardsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative min-h-screen pt-32 pb-20 px-4 overflow-hidden bg-hero-gradient">
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-[#F9F1A9] rounded-full filter blur-[100px] animate-pulse-slow"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#9E7C47] rounded-full filter blur-[100px] animate-pulse-slow"></div>
        {/* Add floating particles background */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
          {Array.from({ length: 20 }).map((_, i) => (
            <div 
              key={i}
              className="absolute bg-white/10 rounded-full"
              style={{
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                width: `${Math.random() * 10 + 2}px`,
                height: `${Math.random() * 10 + 2}px`,
                animation: `float ${Math.random() * 10 + 15}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`,
                opacity: Math.random() * 0.5 + 0.2
              }}
            ></div>
          ))}
        </div>
      </div>
      
      <div className="container mx-auto relative z-10">
        <div className="flex flex-col items-center text-center">
          <div className="inline-flex items-center px-4 py-2 mb-19 rounded-full border border-[#9E7C47]/30 bg-[#9E7C47]/10 animate-fade-in">
            <Zap className="h-6 w-6 text-[#9E7C47] mr-3" />
            <span className="text-base font-semibold">Leverage the power of AI for wealth management</span>
          </div>
          
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight max-w-4xl text-gradient animate-fade-in" style={{ animationDelay: '0.2s' }}>
          Where AI Meets Human Expertise - Now Accessible to Everyone
                    </h1>
          
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mb-8 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            Our AI technology helps you understand and optimizes your entire portfolio across stocks, real estate, gold, bonds, and more. Plus, get access to dedicated wealth managers when you need the human touch.
          </p>
          
          <div className="flex flex-wrap gap-3 justify-center mb-8 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <span className="bg-purple-500/10 border border-purple-500/20 text-white/80 px-3 py-1 rounded-full text-sm hover:scale-105 transition-transform">Stocks</span>
            <span className="bg-[#F9F1A9]/10 border border-[#F9F1A9]/20 text-white/80 px-3 py-1 rounded-full text-sm hover:scale-105 transition-transform">Real Estate</span>
            <span className="bg-yellow-500/10 border border-yellow-500/20 text-white/80 px-3 py-1 rounded-full text-sm hover:scale-105 transition-transform">Gold</span>
            <span className="bg-wealth-skyblue/10 border border-wealth-skyblue/20 text-white/80 px-3 py-1 rounded-full text-sm hover:scale-105 transition-transform">Bonds</span>
            <span className="bg-wealth-light/10 border border-wealth-light/20 text-white/80 px-3 py-1 rounded-full text-sm hover:scale-105 transition-transform">Debt</span>
            <span className="bg-purple-500/10 border border-purple-500/20 text-white/80 px-3 py-1 rounded-full text-sm hover:scale-105 transition-transform">Alternative Assets</span>
            <span className="bg-green-500/10 border border-green-500/20 text-white/80 px-3 py-1 rounded-full text-sm hover:scale-105 transition-transform">Taxes</span>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md justify-center animate-fade-in" style={{ animationDelay: '0.8s' }}>
            <a href="/waitlist" className="w-full sm:w-auto">
              <Button className="w-full bg-[#9E7C47] hover:bg-[#9E7C47]/90 text-white group transition-all duration-300 hover:shadow-lg hover:shadow-[#9E7C47]/30">
                Join the Waitlist
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
            </a>
            <a href="#how-it-works" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10 transition-all duration-300 hover:border-white/40">
                Learn How It Works
              </Button>
            </a>
          </div>
        </div>
        
        <div className="mt-24 flex justify-center animate-fade-in" style={{ animationDelay: '1s' }}>
          <div className="glass-card rounded-xl p-6 md:p-8 max-w-6xl w-full hover:shadow-lg hover:shadow-[#9E7C47]/10 transition-all duration-300">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" ref={featureCardsRef}>
              <div className="flex flex-col items-center text-center feature-card opacity-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-[#9E7C47]/20 mb-4 transform hover:scale-110 transition-transform">
                  <BrainCircuit className="h-6 w-6 text-[#9E7C47]" />
                </div>
                <h3 className="text-xl font-semibold mb-2">AI-Driven Insights</h3>
                <p className="text-white/70">Advanced algorithms analyze market data across all asset classes to optimize your entire investment portfolio.</p>
              </div>
              
              <div className="flex flex-col items-center text-center feature-card opacity-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-wealth-skyblue/20 mb-4 transform hover:scale-110 transition-transform">
                  <Users className="h-6 w-6 text-wealth-skyblue" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Human Expertise</h3>
                <p className="text-white/70">Access to dedicated wealth managers who provide personalized guidance when you need it most.</p>
              </div>
              
              <div className="flex flex-col items-center text-center feature-card opacity-0">
                <div className="w-12 h-12 rounded-full flex items-center justify-center bg-wealth-light/20 mb-4 transform hover:scale-110 transition-transform">
                  <svg className="h-6 w-6 text-wealth-light" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                    <circle cx="9" cy="7" r="4" />
                    <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
                    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-2">Complete Portfolio</h3>
                <p className="text-white/70">Diversified investment strategy across stocks, real estate, gold, bonds, and other asset classes.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
